import ExcelJS from 'exceljs';
import i18n from 'i18next';

export interface ColumnConfig {
    [key: string]: {
        maxWidth?: number;
        wrapText?: boolean;
    };
}

const calculateColumnWidths = (data: any[], headers: { [key: string]: string }, columnConfig?: ColumnConfig) => {
    const MIN_WIDTH = 10;
    const DEFAULT_MAX_WIDTH = 1000;

    return Object.keys(data[0]).map((key) => {
        const headerLength = i18n.t(`excel.${key}`).length;

        const columnData = data.map((row: any) => String(row[key] || '').length);
        const maxContentLength = Math.max(...columnData);

        const maxLength = Math.max(headerLength, maxContentLength);

        const maxWidth = columnConfig?.[key]?.maxWidth ?? DEFAULT_MAX_WIDTH;

        return Math.min(Math.max(maxLength + 2, MIN_WIDTH), maxWidth);
    });
};

export const downloadExcel = ({
    excelDownloadData,
    excelDownloadFolderName,
    hiddenExcelColumn,
    fileNameOverride,
    logo,
    columnConfig,
}: {
    excelDownloadData: any[];
    excelDownloadFolderName?: string;
    hiddenExcelColumn?: string[];
    fileNameOverride?: string;
    logo?: string;
    columnConfig?: ColumnConfig;
}) => {
    const data = excelDownloadData;
    const folderName = excelDownloadFolderName || 'ExcelData';

    const hiddenColumns = hiddenExcelColumn || [];
    // Filter out hidden columns from data
    const filteredData = data.map((row: any) => {
        const filteredRow: any = {};
        Object.keys(row).forEach((key) => {
            if (!hiddenColumns.includes(key)) {
                filteredRow[key] = row[key];
            }
        });
        return filteredRow;
    });

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sayfa-1');

    const columnWidths = calculateColumnWidths(filteredData, {}, columnConfig);
    const columnCount = Object.keys(filteredData[0]).length;

    let startRow = 1;
    const columns = Object.keys(filteredData[0]);

    if (logo) {
        sheet.addRow([]);
        sheet.addRow([]);

        sheet.mergeCells(1, 1, 2, columnCount);

        const imageId = workbook.addImage({
            base64: logo,
            extension: 'png',
        });

        sheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 300, height: 50 },
            editAs: 'oneCell',
        });

        sheet.getRow(1).height = 30;
        sheet.getRow(2).height = 30;

        sheet.addRow([]);
        startRow = 4;

        sheet.columns = columns.map((key, index) => {
            let width = columnWidths[index] + (index === 1 ? 5 : 0);
            return {
                key: key,
                width: width > 10 ? width : 10,
            } as any;
        });

        const headerRow = sheet.addRow(columns.map((key) => i18n.t(`excel.${key}`)));

        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center' } as any;
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            } as any;
        });

        filteredData.forEach((row: any) => sheet.addRow(row));
    } else {
        sheet.columns = columns.map((key, index) => {
            let width = columnWidths[index] + (index === 1 ? 5 : 0);

            return {
                header: `${i18n.t(`excel.${key}`)}`,
                key: key,
                width: width > 10 ? width : 10,
            } as any;
        });
        filteredData.forEach((row: any) => sheet.addRow(row));
    }

    const styleStartRow = logo ? startRow + 1 : 1;
    sheet.eachRow((row, rowNumber) => {
        if (logo && rowNumber > startRow) {
            row.eachCell((cell, colNumber) => {
                const columnKey = columns[colNumber - 1];
                const shouldWrapText = columnConfig?.[columnKey]?.wrapText ?? false;
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: shouldWrapText } as any;
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                } as any;
            });
        } else if (!logo && rowNumber >= styleStartRow) {
            row.eachCell((cell, colNumber) => {
                const columnKey = columns[colNumber - 1];
                const shouldWrapText = columnConfig?.[columnKey]?.wrapText ?? false;
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: shouldWrapText } as any;
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' },
                } as any;
                if (rowNumber === 1) {
                    cell.font = { bold: true };
                }
            });
        }
    });

    const today = new Date();
    const defaultFileName = `${folderName} - ${today.getDate()}.${
        today.getMonth() + 1
    }.${today.getFullYear()} - ${today.getHours()}_${today.getMinutes()}.xlsx`;
    const finalFileName = fileNameOverride && typeof fileNameOverride === 'string' ? fileNameOverride : defaultFileName;

    workbook.xlsx.writeBuffer().then((buffer: any) => {
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = finalFileName;
        link.click();
    });
};

export const readExcelFile = async (file: File): Promise<any[][] | null> => {
    try {
        const workbook = new ExcelJS.Workbook();
        const buffer = await file.arrayBuffer();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        if (!worksheet) return null;
        // getSheetValues returns an array where the first element is null, so slice(1)
        // filter out undefined/null rows
        const data = worksheet.getSheetValues().slice(1).filter(Boolean) as any[][];
        return data;
    } catch (error) {
        console.error('Error reading Excel file:', error);
        return null;
    }
};

export const readExcelFileAsArray = async (file: File): Promise<any[][] | null> => {
    try {
        const workbook = new ExcelJS.Workbook();
        const buffer = await file.arrayBuffer();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        if (!worksheet) return null;
        // getSheetValues returns an array where the first element is null, so slice(1)
        // filter out undefined/null rows
        const data = worksheet.getSheetValues().slice(1).filter(Boolean) as any[][];
        return data;
    } catch (error) {
        console.error('Error reading Excel file as array:', error);
        return null;
    }
};
